import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { toast } from 'sonner'
import {
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useUpdateLanguage } from '@/shared/hooks/api/user'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/shared/lib/utils'

type AuthInterfaceLanguageSelectProps = {
  className?: string
}

export function AuthInterfaceLanguageSelect({
  className,
}: AuthInterfaceLanguageSelectProps) {
  const { t } = useTranslation('common')
  const { data: session, status: sessionStatus } = useGetUserSession()
  const { mutateAsync: updateLanguage, isPending: isUpdatingLanguage } =
    useUpdateLanguage()

  const rawLanguage = i18n.language
  let selectValue: SupportedLanguage = SUPPORTED_LANGUAGES[0].value
  if (isSupportedLanguage(rawLanguage)) {
    selectValue = rawLanguage
  }

  const currentOption =
    SUPPORTED_LANGUAGES.find((lang) => lang.value === selectValue) ??
    SUPPORTED_LANGUAGES[0]

  const persistLanguageToProfile =
    sessionStatus === 'success' && session != null

  function handleLanguageChange(value: string) {
    if (!isSupportedLanguage(value)) {
      return
    }
    void (async () => {
      try {
        if (persistLanguageToProfile) {
          await updateLanguage(value)
        }
        await i18n.changeLanguage(value)
      } catch {
        toast.error(t('settings.languageUpdateFailed'))
      }
    })()
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label className='sr-only' htmlFor='auth-interface-language'>
        {t('settings.interfaceLanguageLabel')}
      </Label>
      <Select
        value={selectValue}
        onValueChange={(value) => {
          if (value) {
            handleLanguageChange(value)
          }
        }}
        disabled={persistLanguageToProfile && isUpdatingLanguage}
        items={SUPPORTED_LANGUAGES.map((lang) => ({
          value: lang.value,
          label: lang.label,
        }))}
      >
        <SelectTrigger
          id='auth-interface-language'
          className='h-9 w-[min(100%,220px)]'
          aria-label={t('settings.interfaceLanguageLabel')}
        >
          <SelectValue>
            <span className='flex items-center gap-2'>
              <span>{currentOption.flag}</span>
              <span>{currentOption.label}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <SelectItem key={lang.value} value={lang.value}>
              <span className='flex items-center gap-2'>
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
